import React, { useState, useCallback, useEffect } from 'react';
import Header from './Header';
import NarrativeDetailPage from './NarrativeDetailPage';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import type { NarrativeSummaryDto } from '../api/fe-management-system-api/narratives';
import { formatNumberToLargestUnit } from '../utils/formatters';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { useToasterStore } from '../stores/ui/toasterStore';

const formatTimelineDate = (iso: string | null | undefined) => {
  if (iso == null) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const NarrativesPage: React.FC = () => {
  const { showToaster } = useToasterStore();
  const [viewMode, setViewMode] = useState<'table' | 'detail'>('table');
  const [narrativeList, setNarrativeList] = useState<NarrativeSummaryDto[]>([]);
  const [narrativeListLoading, setNarrativeListLoading] = useState(true);
  const [selectedNarrativeId, setSelectedNarrativeId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createContent, setCreateContent] = useState('');
  const [creating, setCreating] = useState(false);

  const loadNarrativeList = useCallback(async () => {
    setNarrativeListLoading(true);
    try {
      const res = await feManagementSystemApi.getAllNarratives();
      setNarrativeList(res.data ?? []);
    } catch (err) {
      showToaster(getApiErrorMessage(err as Error, 'Failed to load narratives.'), 'error');
      setNarrativeList([]);
    } finally {
      setNarrativeListLoading(false);
    }
  }, [showToaster]);

  useEffect(() => {
    loadNarrativeList();
  }, [loadNarrativeList]);

  const handleDetailClick = useCallback((id: string) => {
    setSelectedNarrativeId(id);
    setViewMode('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('table');
    setSelectedNarrativeId(null);
  }, []);

  const handleCreateNarrative = useCallback(async () => {
    const name = createName.trim();
    if (!name) {
      showToaster('Narrative name을 입력하세요.', 'info');
      return;
    }
    setCreating(true);
    try {
      await feManagementSystemApi.createNarrative({
        name,
        content: createContent.trim() || undefined,
      });
      showToaster('Narrative이 생성되었습니다.', 'success');
      setCreateModalOpen(false);
      setCreateName('');
      setCreateContent('');
      loadNarrativeList();
    } catch (err) {
      showToaster(getApiErrorMessage(err as Error, 'Failed to create narrative.'), 'error');
    } finally {
      setCreating(false);
    }
  }, [createName, createContent, showToaster, loadNarrativeList]);

  if (viewMode === 'detail' && selectedNarrativeId) {
    return (
      <NarrativeDetailPage
        narrativeId={selectedNarrativeId}
        onBack={handleBackToList}
        onDeleted={loadNarrativeList}
      />
    );
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex flex-col min-h-0">
      <Header title="AF Monitor" showPageControls={false} />
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Narratives</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-500"
            >
              Create Narrative
            </button>
            <button
              type="button"
              onClick={loadNarrativeList}
              disabled={narrativeListLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-gray-700 text-sm font-medium text-gray-200 rounded-md hover:bg-gray-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {narrativeListLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="overflow-y-auto max-h-[calc(100vh-180px)]">
                    <table className="w-full border-collapse">
                      <thead className="sticky top-0 z-10 bg-gray-800">
                        <tr className="border-b border-gray-700">
                          <th className="px-3 py-3 text-left text-sm font-semibold text-gray-300">Narrative Name</th>
                          <th className="px-3 py-3 text-left text-sm font-semibold text-gray-300">Keywords</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-gray-300">Total MC</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-gray-300">Change 24h</th>
                          <th className="px-3 py-3 text-left text-sm font-semibold text-gray-300">Timeline</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-gray-300">Token Count</th>
                          <th className="px-3 py-3 text-left text-sm font-semibold text-gray-300">Top 3 Tokens</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-gray-300">ATH Mcap</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-gray-300">Drop from ATH</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-gray-300 w-20">Detail</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {narrativeList.map((n) => (
                          <tr key={n.id} className="hover:bg-gray-700/30">
                            <td className="px-3 py-2 text-sm text-white">{n.name}</td>
                            <td className="px-3 py-2 text-sm text-gray-300">
                              {n.keywords?.length ? n.keywords.join(', ') : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-white">
                              ${formatNumberToLargestUnit(n.totalMarketCap)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right">
                              {n.change24h != null ? (
                                <span className={n.change24h >= 0 ? 'text-green-400' : 'text-red-400'}>
                                  {n.change24h >= 0 ? '+' : ''}{n.change24h.toFixed(2)}%
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-300">{formatTimelineDate(n.timeline)}</td>
                            <td className="px-3 py-2 text-sm text-center text-white">{n.tokenCount}</td>
                            <td className="px-3 py-2 text-sm text-gray-300">
                              {n.top3Tokens?.length
                                ? n.top3Tokens.map((t) => t.symbol).join(', ')
                                : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-white">
                              {n.athMcap != null ? `$${formatNumberToLargestUnit(n.athMcap)}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-gray-300">
                              {n.dropFromATH != null ? `${n.dropFromATH.toFixed(1)}%` : '-'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => handleDetailClick(n.id)}
                                className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-500"
                              >
                                Detail
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
        )}

        {/* Create Narrative Modal */}
        {createModalOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => {
              setCreateModalOpen(false);
              setCreateName('');
              setCreateContent('');
            }}
          >
            <div
              className="bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-gray-300 mb-4">Create Narrative</h3>
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Narrative name"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <textarea
                  value={createContent}
                  onChange={(e) => setCreateContent(e.target.value)}
                  placeholder="Content (optional)"
                  rows={4}
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-y"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCreateModalOpen(false);
                    setCreateName('');
                    setCreateContent('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateNarrative}
                  disabled={creating}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(NarrativesPage);
